import { create } from 'zustand';
import { Sandbox, CheckoutRequest } from '@/types';
import * as api from '@/services/api';

interface SandboxStore {
  sandboxes: Sandbox[];
  isLoading: boolean;
  error: string | null;
  currentUserObjectId: string | null;

  // Actions
  setCurrentUser: (objectId: string) => void;
  fetchSandboxes: (poolId: string) => Promise<void>;
  checkoutSandbox: (poolId: string, request: CheckoutRequest) => Promise<Sandbox>;
  deleteSandbox: (poolId: string, sandboxId: string) => Promise<void>;
  getSandboxById: (sandboxId: string) => Sandbox | undefined;
  getSandboxesByPool: (poolId: string) => Sandbox[];
}

// Track in-flight fetches and last-fetched timestamps per pool
const fetchingPools = new Set<string>();
const lastFetchedAt = new Map<string, number>();
const STALE_TIME_MS = 30_000; // 30 seconds

export const useSandboxStore = create<SandboxStore>((set, get) => ({
  sandboxes: [],
  isLoading: false,
  error: null,
  currentUserObjectId: null,

  setCurrentUser: (objectId: string) => {
    set({ currentUserObjectId: objectId });
  },

  fetchSandboxes: async (poolId: string) => {
    // Skip if already fetching this pool (dedup concurrent calls)
    if (fetchingPools.has(poolId)) return;

    // Skip if data is still fresh
    const lastFetch = lastFetchedAt.get(poolId) ?? 0;
    if (Date.now() - lastFetch < STALE_TIME_MS) return;

    fetchingPools.add(poolId);
    set({ error: null });

    try {
      const resp = await api.fetchPoolSandboxes(poolId);
      const allPoolSandboxes = resp.value as Sandbox[];
      lastFetchedAt.set(poolId, Date.now());

      // Filter to only show sandboxes owned by the current user
      const userOid = get().currentUserObjectId;
      const poolSandboxes = userOid
        ? allPoolSandboxes.filter(sb => sb.owner?.objectId === userOid)
        : allPoolSandboxes;

      set(state => {
        const otherPoolSandboxes = state.sandboxes.filter(sb => sb.poolId !== poolId);
        return {
          sandboxes: [...otherPoolSandboxes, ...poolSandboxes],
          isLoading: false,
        };
      });
    } catch (err) {
      console.error('[SandboxStore] fetchSandboxes failed:', err);
      set({ error: 'Failed to fetch sandboxes', isLoading: false });
    } finally {
      fetchingPools.delete(poolId);
    }
  },

  checkoutSandbox: async (poolId: string, request: CheckoutRequest) => {
    set({ isLoading: true, error: null });

    try {
      const sandbox = (await api.checkoutSandbox(poolId, request)) as Sandbox;
      set(state => ({
        sandboxes: [...state.sandboxes, sandbox],
        isLoading: false,
      }));
      // Invalidate cache so next navigation re-fetches
      lastFetchedAt.delete(poolId);
      return sandbox;
    } catch (err) {
      console.error('[SandboxStore] checkoutSandbox failed:', err);
      set({ error: 'Failed to checkout sandbox', isLoading: false });
      throw err;
    }
  },

  deleteSandbox: async (poolId: string, sandboxId: string) => {
    set({ isLoading: true, error: null });

    try {
      await api.deleteSandbox(poolId, sandboxId);
      set(state => ({
        sandboxes: state.sandboxes.filter(sb => sb.sandboxId !== sandboxId),
        isLoading: false,
      }));
      // Invalidate cache so next navigation re-fetches
      lastFetchedAt.delete(poolId);
    } catch (err) {
      console.error('[SandboxStore] deleteSandbox failed:', err);
      set({ error: 'Failed to delete sandbox', isLoading: false });
      throw err;
    }
  },

  getSandboxById: (sandboxId: string) => {
    return get().sandboxes.find(sb => sb.sandboxId === sandboxId);
  },

  getSandboxesByPool: (poolId: string) => {
    return get().sandboxes.filter(sb => sb.poolId === poolId);
  },
}));
